/**
 * Risk catalog — implements docs/RISK-CHECKS.md.
 *
 * This is the product. A PSBT decoder is a commodity; a decoder that says "the change output
 * in this PSBT is not yours" is not.
 *
 * Every check is a pure function of (intent, vault context, optional chain data). No I/O.
 * Checks needing chain data degrade to UNVERIFIABLE rather than silently passing.
 *
 * GOVERNING RULE: unverifiable is not safe. Every tool that shows a green tick for "we did
 * not look" trains users to trust the green.
 */
import * as btc from '@scure/btc-signer';
/** The script type a vault's own change takes, per descriptor class. */
/**
 * The script type a vault's own change takes, per descriptor class — and ONLY where that type is
 * distinctive enough for a lookalike to mean something.
 *
 * `CHANGE_LOOKALIKE` says "another output here uses the same kind of address your change does,
 * and it is not yours". For a shared `wsh` wallet that is a real signal: ordinary payments go to
 * single-key addresses, so a second P2WSH in the same transaction is unusual and worth a look.
 *
 * ⚠️ FOR A SINGLE-KEY WALLET IT IS NOISE, and adding `wpkh` here briefly made it so. Almost every
 * payment anyone makes goes to a `wpkh` address, so a `wpkh` wallet paying a supplier produced
 * `CHANGE_LOOKALIKE` on a completely ordinary transaction — a warning on every single payment,
 * which is the fastest way to teach somebody to click past warnings. Caught by the first honest
 * single-signature fixture written against it.
 *
 * A class belongs here only if its own change type is RARE among ordinary payments. Single-key
 * classes are deliberately absent, and their omission is the check being correctly skipped
 * rather than a gap.
 */
const VAULT_SCRIPT_TYPE = {
    'wsh-sortedmulti': 'wsh', 'tr-multi': 'tr', miniscript: 'wsh',
};
/** Dust thresholds by output type (sats), per Bitcoin Core relay policy. */
const DUST = { wpkh: 294, wsh: 330, tr: 330, pkh: 546, sh: 540 };
/**
 * Format sats as BTC without ever touching a float.
 *
 * `sats / 1e8` rounds above 2^53, so the number a human reads to decide whether to sign could
 * differ from the number being spent. Integer division cannot.
 */
function btcAmt(sats) {
    const neg = sats < 0n;
    const abs = neg ? -sats : sats;
    return `${neg ? '-' : ''}${abs / 100000000n}.${(abs % 100000000n).toString().padStart(8, '0')}`;
}
/** 21 million BTC in sats. No valid transaction can move more, in any single amount or total. */
const MAX_MONEY = 2100000000000000n;
export function runRiskCatalog(intent, ctx, chain = {}) {
    const F = [];
    const add = (severity, code, detail) => F.push({ code, severity, detail });
    /* ── F. Structural ───────────────────────────────────────────────────────── */
    /*
     * ⚠️ THIS CHECK HAS BEEN A VALUE COMPARED WITH ITSELF TWICE. Read before editing.
     *
     * v1 compared `intent.network` against `ctx.network`, and `deriveIntent` copies the first
     * from the second — dead for every input ever. v2 compared `ctx.descriptor.network` against
     * `ctx.network`, which reads like two sources and is not: the only production caller resolves
     * its network AS `parsed.network`, so the two sides were still one value one level up. An
     * audit found the second version on 2026-08-27; the first had already been written up in the
     * playbook as the canonical example of the mistake.
     *
     * The rule that stops a v3: **`ctx.declaredNetwork` must come from something that did not
     * parse this descriptor.** In Org mode that is `vaults.network`, recorded at registration by
     * the server and read back later. In Solo there IS no second source — the descriptor's
     * extended-key prefix is the sole authority and `state.vault.network` is overwritten from it
     * on every parse — so Solo passes nothing and this clause correctly does not fire. A check
     * that cannot fire is honest only when it is ABSENT; the failure was claiming it ran.
     *
     * What still carries independent chain identity, and is checked below: the BIP-44 coin type
     * in the inputs' derivation paths, which comes out of the PSBT rather than the descriptor.
     */
    if (ctx.declaredNetwork !== undefined && ctx.descriptor.network !== ctx.declaredNetwork)
        add('BLOCK', 'NETWORK_MISMATCH', `the vault descriptor holds ${ctx.descriptor.network} keys but this vault was registered ` +
            `as ${ctx.declaredNetwork}; every address shown would be encoded for the wrong chain`);
    const expectedCoin = ctx.network === 'mainnet' ? 0 : 1;
    const wrongCoin = (intent.coinTypes ?? []).filter((c) => c !== expectedCoin);
    if (wrongCoin.length > 0)
        add('BLOCK', 'NETWORK_MISMATCH', 
        // Coin type 0 is mainnet and 1 is test/signet; anything else is neither, and calling it
        // "test/signet" because it is not 0 was simply wrong — `coin type 2 (test/signet)` is not
        // a sentence about the world.
        `the inputs' derivation paths declare coin type ${wrongCoin.join(', ')} ` +
            `(${wrongCoin.map(coinName).join(', ')}) but this vault is ${ctx.network}`);
    // Duplicate inputs inflate the input total and therefore the fee. `@scure/btc-signer` does not
    // reject them, so a transaction that can only ever spend 7 BTC reported 14 in with a 0.1 BTC
    // fee — and PASS on both the input and fee checks.
    // Read defensively. The catalog must never THROW on an intent built by an older client or by
    // hand — a risk engine that crashes reports nothing at all, which is the worst outcome of the
    // three (report, refuse, crash).
    if ((intent.duplicateOutpoints?.length ?? 0) > 0)
        add('BLOCK', 'DUPLICATE_INPUT', `the same coin is spent more than once: ${(intent.duplicateOutpoints ?? []).join(', ')}. ` +
            `Every amount derived from the inputs, including the fee, is wrong.`);
    const missingUtxo = intent.inputs.filter((i) => i.amountSats === null);
    if (missingUtxo.length > 0)
        add('BLOCK', 'INPUT_MISSING_UTXO', `input${missingUtxo.length > 1 ? 's' : ''} ${missingUtxo.map((i) => i.index).join(', ')} ` +
            `carry no UTXO data`);
    /* ── A. Ownership & theft ────────────────────────────────────────────────── */
    /*
     * The catalog's premise for this BLOCK is "ownership cannot be established at all". That
     * premise is false whenever the descriptor could establish it anyway — and it usually can,
     * because `isMine` compares SCRIPTS and needs no metadata from the PSBT.
     *
     * Meanwhile **Bitcoin Core deletes `bip32Derivation` from an input once it has signed it**.
     * `intent.ts` goes to real trouble to keep `hasDerivation` out of `intentHash` for exactly
     * that reason, and then this check BLOCKED every Core-signed PSBT — and a BLOCK cannot be
     * acknowledged, so an honest, correctly-signed transaction became unusable at the last step.
     * Block only where ownership genuinely could not be resolved.
     */
    const noDerivation = intent.inputs.filter((i) => !i.hasDerivation);
    const unresolved = noDerivation.filter((i) => i.mine !== true);
    if (unresolved.length > 0)
        add('BLOCK', 'MISSING_BIP32_DERIVATION', `${unresolved.length} input${unresolved.length > 1 ? 's' : ''} carry no derivation ` +
            `paths and derive from no branch in the scan window, so ownership cannot be established`);
    else if (noDerivation.length > 0)
        add('INFO', 'NO_DERIVATION', `${noDerivation.length} input${noDerivation.length > 1 ? 's' : ''} carry no derivation ` +
            `paths — normal for a PSBT Bitcoin Core has signed, and ownership was confirmed from the ` +
            `descriptor instead`);
    const change = intent.outputs.filter((o) => o.role === 'change');
    const external = intent.outputs.filter((o) => o.role === 'external');
    // A transaction that spends our inputs but returns nothing to us, while paying an
    // unrecognised script, is the classic coordinator redirection.
    const spendsOurs = intent.inputs.some((i) => i.mine === true);
    // `external.length > 1` used to guard this, so the SIMPLEST attack — sweep everything to one
    // unrecognised address — raised nothing at all, and the worst severity on screen was
    // UNVERIFIABLE. The highest-value check in the product, silent on the easiest case.
    if (change.length === 0 && spendsOurs && external.length >= 1) {
        const biggest = [...external].sort((a, b) => (a.amountSats < b.amountSats ? 1 : a.amountSats > b.amountSats ? -1 : 0))[0];
        add('CRITICAL', 'CHANGE_NOT_MINE', `no output returns to this vault; the largest, ${btcAmt(biggest.amountSats)} BTC to ` +
            `${biggest.address ?? biggest.script.slice(0, 20)}, derives from no branch within the ` +
            `scan window`);
    }
    else if (change.length > 0) {
        /*
         * Finding one output that IS ours is not the same as having accounted for the others.
         *
         * The check used to stop at the first change output and render a green tick. An attacker
         * does not have to remove your change — adding a SECOND change-shaped output and keeping
         * it is quieter, because the panel still says "change verified" and the amount you were
         * expecting is still there. The tell is script type: a `wsh(sortedmulti)` vault's own
         * change is a P2WSH, so another unrecognised P2WSH in the same transaction is the one
         * output most likely to be mistaken for it. Say so instead of ticking.
         */
        const vaultType = VAULT_SCRIPT_TYPE[ctx.descriptor.classId] ?? null;
        const lookalikes = external.filter((o) => vaultType !== null && o.scriptType === vaultType);
        const verified = `change of ${btcAmt(change[0].amountSats)} BTC derives from this vault ` +
            `at m/${change[0].path}`;
        if (lookalikes.length > 0) {
            add('WARN', 'CHANGE_LOOKALIKE', `${verified}, but output${lookalikes.length > 1 ? 's' : ''} ` +
                `${lookalikes.map((o) => o.index).join(', ')} also pay ${vaultType} scripts — the same ` +
                `type this vault uses for its own change — and derive from no branch within the scan ` +
                `window. Confirm each is a payment you intended, not change being kept by someone else.`);
        }
        else {
            add('PASS', 'CHANGE_VERIFIED', verified);
        }
    }
    /*
     * Change sent to the RECEIVE branch. docs/RISK-CHECKS.md A carries CHANGE_PATH_ANOMALY for it
     * and nothing implemented it: any output the scan recognised was labelled "change" and ticked,
     * whichever branch it came from. Reusing a receive address for change is a privacy leak and,
     * more practically, a sign the transaction was not built by the wallet that owns the vault.
     */
    const receiveBranches = new Set(ctx.descriptor.keys.map((k) => k.multipath?.[0]).filter((b) => b !== undefined));
    /*
     * ⚠️ THIS CHECK ONLY EXISTS FOR MULTIPATH DESCRIPTORS, and now says so instead of ticking.
     *
     * `receiveBranches` is built from `k.multipath[0]`, so for a `/n/*` or a bare `/*` key it is
     * EMPTY and the filter below can never match — the clause was structurally dead and a
     * receive-address-as-change went out under `PASS CHANGE_VERIFIED`. Worse, for those descriptor
     * shapes `derive` ignores the scan branch entirely, so branches 0 and 1 produce identical
     * scripts and the receive/change distinction does not exist at all: every vault-owned output
     * is labelled change.
     *
     * That is a genuine limitation of the descriptor shape rather than a bug to fix here, and the
     * governing rule is that a check which did not run must say so. UNVERIFIABLE, not silence.
     */
    if (receiveBranches.size === 0 && intent.outputs.some((o) => o.mine)) {
        add('UNVERIFIABLE', 'CHANGE_PATH_UNCHECKED', `this descriptor does not use <receive;change> multipath notation, so its receive and ` +
            `change branches derive the same scripts — whether the change output sits on the branch ` +
            `this wallet would have used cannot be determined from the descriptor`);
    }
    else {
        const onReceive = intent.outputs.filter((o) => o.mine && o.path !== null && receiveBranches.has(Number(o.path.split('/')[0])));
        if (onReceive.length > 0)
            add('WARN', 'CHANGE_PATH_ANOMALY', `output${onReceive.length > 1 ? 's' : ''} ${onReceive.map((o) => o.index).join(', ')} ` +
                `return to this vault on the RECEIVE branch (m/${onReceive.map((o) => o.path).join(', m/')}) ` +
                `rather than the change branch — the coins are yours, but the transaction was not built ` +
                `the way this wallet builds one`);
    }
    const notMine = intent.inputs.filter((i) => i.mine === false);
    if (notMine.length > 0)
        add('CRITICAL', 'INPUT_NOT_MINE', `input${notMine.length > 1 ? 's' : ''} ${notMine.map((i) => i.index).join(', ')} do not ` +
            `derive from this vault — you may be co-signing someone else's spend`);
    else if (intent.inputs.length > 0 && intent.inputs.every((i) => i.mine === true))
        add('PASS', 'INPUTS_VERIFIED', `all ${intent.inputs.length} inputs derive from this vault`);
    /* ── B. Signature scope ──────────────────────────────────────────────────── */
    const sighashes = [...new Set(intent.inputs.map((i) => i.sighash))];
    if (sighashes.length > 1)
        add('CRITICAL', 'MIXED_SIGHASH', `inputs request different sighash types: ${sighashes.join(', ')}`);
    else if (sighashes.length === 1 && sighashes[0] !== btc.SigHash.ALL)
        add('CRITICAL', 'NON_DEFAULT_SIGHASH', `sighash ${sighashes[0]} on every input — not SIGHASH_ALL`);
    else if (sighashes.length === 1)
        add('PASS', 'SIGHASH_ALL', 'SIGHASH_ALL on every input');
    /*
     * SIGHASH_SINGLE commits to the output at the SAME index as the input. An input whose index
     * has no matching output is the historical fund-loss case: on legacy inputs the sighash
     * degenerates to the value 1, and anyone can then replay that signature. BLOCK in the catalog,
     * absent from the code.
     */
    const SINGLE = new Set([btc.SigHash.SINGLE, btc.SigHash.SINGLE_ANYONECANPAY]);
    const orphanSingle = intent.inputs.filter((i) => SINGLE.has(i.sighash) && i.index >= intent.outputs.length);
    if (orphanSingle.length > 0)
        add('BLOCK', 'SIGHASH_SINGLE_NO_OUTPUT', `input${orphanSingle.length > 1 ? 's' : ''} ${orphanSingle.map((i) => i.index).join(', ')} ` +
            `request SIGHASH_SINGLE but there is no output at that index — the signature would commit ` +
            `to nothing and could be replayed`);
    const nonStandard = intent.outputs.filter((o) => !o.standard);
    if (nonStandard.length > 0)
        add('CRITICAL', 'NONSTANDARD_OUTPUT', `output${nonStandard.length > 1 ? 's' : ''} ${nonStandard.map((o) => o.index).join(', ')} ` +
            `use a script we cannot classify — may be unspendable`);
    /*
     * No transaction can move more than 21 million BTC. An amount above it is either corrupt bytes
     * or a deliberate overflow probe; either way nothing downstream should treat the numbers on
     * screen as meaningful.
     *
     * ⚠️ This sits OUTSIDE the fee branch on purpose. It used to live inside the `else` of
     * `if (intent.feeSats === null)`, so a PSBT with missing UTXO data — the case where the
     * numbers are least trustworthy — was the one case where an over-max amount went unreported.
     */
    const overMax = [
        ...intent.outputs.map((o) => o.amountSats),
        intent.totalInSats ?? 0n, intent.totalOutSats,
    ].filter((v) => v > MAX_MONEY);
    if (overMax.length > 0)
        add('BLOCK', 'AMOUNT_ABOVE_MAX_MONEY', `an amount of ${btcAmt(overMax[0])} BTC exceeds the 21,000,000 BTC that will ever ` +
            `exist, so this cannot be a valid transaction`);
    /* ── C. Value & fee ──────────────────────────────────────────────────────── */
    if (intent.feeSats === null) {
        add('BLOCK', 'FEE_UNVERIFIABLE', 'input amounts are unknown, so the fee cannot be computed — this is the gap behind the ' +
            '2020 hardware-wallet fee-theft attack');
    }
    else {
        /*
         * "Computed, not assumed" was only ever half true.
         *
         * The fee is `totalIn - totalOut`, and `totalIn` comes from whatever UTXO data the file
         * carried. `non_witness_utxo` carries the previous transaction, which the parser has already
         * checked hashes to this input's txid — that amount is the chain's. A bare `witness_utxo`
         * carries a number the file's author wrote, tied to nothing. Reporting both as
         * `PASS FEE_VERIFIABLE` put a green tick on a fee derived from an unverified claim, which is
         * exactly the "green tick for we did not look" this catalog exists to refuse.
         *
         * UNVERIFIABLE, not WARN, and the distinction is load-bearing. Witness-only PSBTs are
         * ordinary — plenty of coordinators emit them for segwit inputs — so a severity that demands
         * an acknowledgement tick would put one on routine traffic, and this file has already
         * learned where that road goes (see the CHANGE_LOOKALIKE note: a warning on every payment is
         * the fastest way to teach somebody to click past warnings). "Not checked" is the honest
         * label for a number nobody could check.
         */
        const unbound = intent.inputs.filter((i) => i.amountSats !== null && !i.amountBound);
        if (unbound.length === 0)
            add('PASS', 'FEE_VERIFIABLE', 'fee computed from input amounts bound to the coins being spent, not assumed');
        else
            add('UNVERIFIABLE', 'FEE_AMOUNTS_ASSERTED', `the fee is computed, but ${unbound.length} of ${intent.inputs.length} input ` +
                `amount${unbound.length > 1 ? 's are' : ' is'} taken from the file's own claim rather ` +
                `than from the previous transaction, so the fee shown rests on a number nobody verified`);
        if (intent.feeSats < 0n)
            add('BLOCK', 'NEGATIVE_FEE', 'outputs exceed inputs; this transaction is invalid');
        else if (intent.feeSats >= intent.totalOutSats)
            add('BLOCK', 'ABSURD_FEE', `fee of ${btcAmt(intent.feeSats)} BTC is at or above the ` +
                `${btcAmt(intent.totalOutSats)} BTC total being sent`);
        /*
         * THE FEE AS A FRACTION OF WHAT IS BEING SPENT — the clause that catches a consolidation.
         *
         * All three fee alarms used to fail TOGETHER on the one transaction shape with no external
         * recipient, and a self-send is a routine treasury operation:
         *
         *   · `ABSURD_FEE` compares against TOTAL OUTPUT, and change is output — so a fee just under
         *     the change amount is "fine";
         *   · the proportion clause below is gated on `sentExternal > 0n`, and a consolidation sends
         *     nothing externally;
         *   · the sat/vB clause needs `chain.feeBand`, which Solo never has by construction.
         *
         * An audit built 3 vault inputs totalling 14.2 BTC paying a single 7.2 BTC change output —
         * **7 BTC of fee, 49% of the vault** — and the catalog returned four green ticks including
         * `PASS CHANGE_VERIFIED`, nothing to acknowledge, and signing enabled.
         *
         * The denominator that always exists is TOTAL INPUT. Whatever a transaction is for, the fee
         * is a fraction of what it moves, and that is a number every PSBT with UTXO data carries.
         * CRITICAL rather than BLOCK: a genuinely urgent consolidation at a wild fee rate is a thing
         * a treasury may really want to do, so this demands a typed acknowledgement rather than
         * making it impossible.
         */
        const totalIn = intent.totalInSats;
        if (totalIn !== null && totalIn > 0n && intent.feeSats > 0n) {
            const pct = Number(intent.feeSats * 10000n / totalIn) / 100;
            if (intent.feeSats * 100n > totalIn * 5n)
                add('CRITICAL', 'FEE_SHARE_OF_SPEND', `the fee of ${btcAmt(intent.feeSats)} BTC is ${pct.toFixed(1)}% of the ` +
                    `${btcAmt(totalIn)} BTC these inputs hold`);
            else if (intent.feeSats * 100n > totalIn)
                add('WARN', 'FEE_SHARE_OF_SPEND', `the fee of ${btcAmt(intent.feeSats)} BTC is ${pct.toFixed(1)}% of the ` +
                    `${btcAmt(totalIn)} BTC these inputs hold`);
        }
        /*
         * The half of FEE_OUTLIER that needs no mempool data: "fee > 10% of the amount sent".
         * Only the sat/vB clause was implemented, so an offline client — which is every Solo
         * client, by design — could not catch a fee ten times the payment. `ABSURD_FEE` did not
         * catch it either, because it compares against TOTAL output, and change is output: a
         * transaction paying 0.001 BTC with a 0.01 BTC fee and 0.089 BTC of change looked fine
         * from every angle the catalog actually checked.
         */
        const sentExternal = external.reduce((a, o) => a + o.amountSats, 0n);
        const feePctOfSent = sentExternal > 0n
            ? (Number(intent.feeSats * 10000n / sentExternal) / 100).toFixed(1) : '—';
        const feeOutOfProportion = sentExternal > 0n && intent.feeSats * 10n > sentExternal;
        let outlierReported = false;
        if (feeOutOfProportion) {
            add('CRITICAL', 'FEE_OUTLIER', `the fee of ${btcAmt(intent.feeSats)} BTC is ${feePctOfSent}% of the ` +
                `${btcAmt(sentExternal)} BTC actually being sent`);
            outlierReported = true;
        }
        const rate = intent.feeRate;
        if (chain.feeBand && rate !== null) {
            const { low, high } = chain.feeBand;
            if (rate > high * 3) {
                if (!outlierReported)
                    add('CRITICAL', 'FEE_OUTLIER', `${rate} sat/vB against a ${low}–${high} band — ${feePctOfSent}% of the amount sent`);
            }
            else if (rate < low) {
                add('WARN', 'FEE_TOO_LOW', `${rate} sat/vB is below the ${low}–${high} band; may stall`);
            }
            else if (rate > high) {
                // Anything in (high, 3*high] used to fall through to the PASS below and render GREEN:
                // 50 sat/vB against a 5–20 band reported "within the 5–20 band". A direct violation of
                // the governing rule — a tool that shows a tick for something it knows is out of range
                // teaches people to stop reading the ticks.
                add('WARN', 'FEE_ABOVE_BAND', `${rate} sat/vB is above the ${low}–${high} band — higher than the network requires, ` +
                    `though not by enough to look like an error`);
            }
            else {
                add('PASS', 'FEE_IN_BAND', `${rate} sat/vB, within the ${low}–${high} band`);
            }
        }
        else if (rate !== null) {
            add('UNVERIFIABLE', 'FEE_RATE_UNCHECKED', `${rate} sat/vB — no mempool data available, so this could not be compared`);
        }
    }
    for (const o of intent.outputs) {
        // The floor follows the SCRIPT TYPE. Choosing it from the address prefix was wrong twice
        // over: `bc1q`/`tb1q` covers both P2WPKH (20-byte program) and P2WSH (32-byte), so a
        // 300-sat P2WSH got the 294 floor and no warning; and every non-bech32 address fell to
        // 'wsh' (330), so a 400-sat P2PKH — real floor 546 — got none either.
        // A data output carries zero value ON PURPOSE and has no spender to be uneconomic for.
        // There is no dust floor to be under.
        if (o.scriptType === 'nulldata')
            continue;
        const floor = BigInt(DUST[o.scriptType] ?? 330);
        if (o.amountSats < floor)
            add('WARN', 'DUST_OUTPUT', `output ${o.index} pays ${o.amountSats} sat, below the ~${floor} sat dust floor`);
    }
    /* ── Coordination: data the caller supplied and we previously ignored ────── */
    // `POLICY_NOT_MET` and `INPUT_SPENT_ELSEWHERE` are both BLOCK in docs/RISK-CHECKS.md, and both
    // inputs were accepted by the API and then dropped on the floor — a caller who did the work to
    // supply them got neither the check nor an UNVERIFIABLE saying it had not run.
    if (chain.spentElsewhere) {
        const conflicted = intent.inputs
            .filter((i) => i.outpoint && chain.spentElsewhere.has(i.outpoint))
            .map((i) => i.outpoint);
        if (conflicted.length > 0)
            add('BLOCK', 'INPUT_SPENT_ELSEWHERE', `${conflicted.length} input(s) already spent by another transaction: ` +
                `${conflicted.join(', ')}. Signing this is a wasted ceremony — it can never confirm.`);
    }
    else {
        add('UNVERIFIABLE', 'CONFLICT_UNCHECKED', 'no chain data, so whether these coins are already spent elsewhere was not checked');
    }
    /* ── D. Hygiene ──────────────────────────────────────────────────────────── */
    if (!intent.rbf)
        add('WARN', 'RBF_DISABLED', 'no input signals replaceability; this cannot be fee-bumped');
    if (intent.locktime > 0)
        // Bitcoin Core sets nLockTime to the current height on nearly every transaction it builds
        // (anti-fee-sniping), so warning on any non-zero value fires on the common case and demands
        // an acknowledgement for nothing. Only a locktime in the FUTURE actually delays spending,
        // and deciding that needs a tip height — which we may not have.
        if (chain.tipHeight === undefined) {
            add('INFO', 'LOCKTIME_SET', `nLockTime is ${intent.locktime}. Bitcoin Core sets this to the current height as ` +
                `standard practice; without a chain tip we cannot tell whether it is in the future.`);
        }
        else if (intent.locktime > chain.tipHeight) {
            add('WARN', 'LOCKTIME_FUTURE', `nLockTime ${intent.locktime} is above the current tip ${chain.tipHeight} — this ` +
                `transaction cannot confirm for another ${intent.locktime - chain.tipHeight} blocks`);
        }
    if (chain.seenScripts) {
        const reused = intent.outputs.filter((o) => chain.seenScripts.has(o.script));
        if (reused.length > 0)
            add('WARN', 'ADDRESS_REUSE', `output${reused.length > 1 ? 's' : ''} ${reused.map((o) => o.index).join(', ')} pay an ` +
                `address with existing on-chain history`);
    }
    else {
        add('UNVERIFIABLE', 'ADDRESS_REUSE_UNCHECKED', 'no chain data, so address reuse could not be checked');
    }
    if (ctx.declared?.externalOutputCount !== undefined &&
        external.length > ctx.declared.externalOutputCount)
        add('CRITICAL', 'UNDECLARED_RECIPIENT', `${external.length} external outputs but ${ctx.declared.externalOutputCount} were declared`);
    if (ctx.declared?.totalToExternalSats !== undefined) {
        const actual = external.reduce((a, o) => a + o.amountSats, 0n);
        if (actual !== ctx.declared.totalToExternalSats)
            add('CRITICAL', 'INTENT_AMOUNT_MISMATCH', `pays ${btcAmt(actual)} BTC externally but ${btcAmt(ctx.declared.totalToExternalSats)} ` +
                `was declared`);
    }
    /*
     * `ctx.policy` was accepted by the API and never read — a caller who did the work to supply
     * the vault policy got neither the check nor an UNVERIFIABLE saying it had not run, which is
     * the exact failure the governing rule exists to prevent.
     */
    if (ctx.policy) {
        const { m, n, signers, finalizing } = ctx.policy;
        if (m !== ctx.descriptor.m || n !== ctx.descriptor.n)
            add('BLOCK', 'POLICY_DESCRIPTOR_MISMATCH', `the vault policy says ${m}-of-${n} but the descriptor being used is ` +
                `${ctx.descriptor.m}-of-${ctx.descriptor.n}; the policy on record is not the policy ` +
                `these coins are actually held under`);
        if (signers === undefined) {
            add('UNVERIFIABLE', 'POLICY_UNCHECKED', `${m}-of-${n} policy given, but no signature attribution was supplied, so whether the ` +
                `threshold is met could not be checked here`);
        }
        else {
            // Anything the attribution could not tie to a vault key must never count towards m.
            const counted = signers.filter((f) => !f.startsWith('unknown:'));
            /*
             * A STRANGER HAS WRITTEN INTO THIS PSBT — reported, not merely excluded.
             *
             * `psbt.ts` builds these `unknown:` entries and its docstring says they "exist so that
             * 'somebody who is not one of your signers has written into this PSBT' is reportable at
             * all, rather than being silently discarded." The catalog's only use of them was
             * `.filter(f => !f.startsWith('unknown:'))` — silent discard, the exact outcome the
             * prefix was invented to prevent. An audit put two foreign signatures on a PSBT and the
             * worst severity on screen was UNVERIFIABLE.
             *
             * CRITICAL, not BLOCK. The signatures are inert — they satisfy no branch of the script
             * and `finalize()` ignores them — so the transaction is not unsafe to sign. What it means
             * is that the PSBT passed through something that had no business editing it, and that is
             * a fact a human must consciously accept rather than scroll past.
             */
            const foreign = signers.filter((f) => f.startsWith('unknown:'));
            if (foreign.length > 0)
                add('CRITICAL', 'FOREIGN_SIGNATURE', `${foreign.length} signature${foreign.length > 1 ? 's' : ''} in this PSBT ` +
                    `belong${foreign.length > 1 ? '' : 's'} to no key in this vault ` +
                    `(${foreign.join(', ')}). They cannot satisfy the script and will not be counted, ` +
                    `but something edited this PSBT that is not one of your signers`);
            if (counted.length >= m)
                add('PASS', 'POLICY_MET', `${counted.length} of the ${m} required signatures collected: ${counted.join(', ')}`);
            else if (finalizing)
                add('BLOCK', 'POLICY_NOT_MET', `${counted.length} of ${m} required signatures — this cannot be finalized`);
            else
                add('INFO', 'POLICY_PROGRESS', `${counted.length} of ${m} required signatures collected so far`);
        }
    }
    if (intent.inputs.length >= 20)
        add('INFO', 'HIGH_INPUT_COUNT', `${intent.inputs.length} inputs; size and fee exposure are correspondingly large`);
    return F;
}
/** What a BIP-44 coin type actually denotes. Registered values only; anything else is unknown. */
const coinName = (c) => c === 0 ? 'mainnet' : c === 1 ? 'test/signet' : `unregistered coin type ${c}`;
/** Highest severity present, using the catalog's own ordering. */
const ORDER = ['BLOCK', 'CRITICAL', 'WARN', 'UNVERIFIABLE', 'INFO', 'PASS'];
export function worstSeverity(findings) {
    for (const s of ORDER)
        if (findings.some((f) => f.severity === s))
            return s;
    return 'PASS';
}
/** Signing is disabled outright by a BLOCK; a CRITICAL requires typed acknowledgement. */
export function signingDisabled(findings) {
    return findings.some((f) => f.severity === 'BLOCK');
}
export function requiresAcknowledgement(findings) {
    return findings.filter((f) => f.severity === 'CRITICAL' || f.severity === 'WARN');
}
//# sourceMappingURL=risk.js.map